import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

const DEFAULT_ACCENT = '#EF9F27';

const bezDiakritiky = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

// Vlastní stylovaný dropdown (nahrazuje nativní <select> s ošklivým OS seznamem).
// Rozbalený panel se vykresluje přes portál do <body> s position:fixed, aby nebyl
// překryt sousedními kartami / ořezán overflow (stacking-context problém).
//
// options: [{ value, label, ikona?, popis?, odznaky?, uroven?, hlavicka?, tlumene? }]
//   ikona    — uzel před textem (miniatura položky, barevná tečka…)
//   popis    — druhý řádek pod textem (kód, doplňující info)
//   odznaky  — uzly za textem (hvězda, ikona pokladny…)
//   uroven   — odsazení pro stromovou strukturu (0 = kořen), kreslí i vodicí linku
//   hlavicka — nevybíratelný nadpis skupiny
//   tlumene  — archivovaná položka; ztlumí se, ale zůstane vidět
// Bez těchto polí se položka vykreslí jako dřív (jen text), takže starší
// použití zůstávají beze změny.
export default function Dropdown({ value, onChange, options, placeholder = '—', isDark, leftIcon, height = 38, minWidth, accent = DEFAULT_ACCENT }) {
    const ACCENT = accent;
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null); // { left, width, top? , bottom? }
    const [q, setQ] = useState('');
    const ref = useRef(null);       // trigger button wrapper
    const panelRef = useRef(null);  // portálový panel
    const searchRef = useRef(null); // vyhledávací input

    const spocitejPozici = () => {
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        const otevritNahoru = window.innerHeight - r.bottom < 260 && r.top > 260;
        // Panel se roztáhne podle nejdelší položky (viz style níž). Když je málo
        // místa vpravo (např. úzký selektor u pravého okraje), kotvíme ho zprava,
        // ať roste doleva a plný text se vejde.
        const kotviZprava = window.innerWidth - r.left < 340;
        setCoords({
            width: r.width,
            ...(kotviZprava
                ? { right: Math.max(8, window.innerWidth - r.right) }
                : { left: r.left }),
            ...(otevritNahoru
                ? { bottom: window.innerHeight - r.top + 4 }
                : { top: r.bottom + 4 }),
        });
    };

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (ref.current?.contains(e.target)) return;
            if (panelRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onReflow = () => spocitejPozici();
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onReflow);
        // capture=true zachytí scroll i uvnitř scrollovatelných rodičů
        window.addEventListener('scroll', onReflow, true);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [open]);

    const selected = options.find(o => String(o.value) === String(value ?? ''));
    const box = isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)';
    const border = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const panelBg = isDark ? 'rgba(28,28,40,0.98)' : '#ffffff';

    const hledatelne = options.filter(o => !o.hlavicka).length > 7;
    const filtrovane = hledatelne && q.trim()
        ? options.filter(o => !o.hlavicka && bezDiakritiky(o.label).includes(bezDiakritiky(q)))
        : options;

    const toggle = () => {
        if (!open) { spocitejPozici(); setQ(''); }
        setOpen(o => !o);
    };

    // Fokus na vyhledávací pole hned po otevření
    useEffect(() => {
        if (open && hledatelne) {
            const t = setTimeout(() => searchRef.current?.focus(), 0);
            return () => clearTimeout(t);
        }
    }, [open, hledatelne]);

    return (
        <div ref={ref} style={{ position: 'relative', minWidth }}>
            <button type="button" onClick={toggle}
                className="w-full flex items-center gap-2"
                style={{ height, background: box, border: `1px solid ${open ? ACCENT : border}`, borderRadius: 6, padding: '0 8px 0 10px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
                {leftIcon}
                <span className="flex-1 min-w-0 truncate text-left" title={selected ? selected.label : ''} style={{ color: selected ? 'var(--text-primary)' : (isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)') }}>
                    {selected ? selected.label : placeholder}
                </span>
                <ChevronDown size={15} style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>

            {open && coords && createPortal(
                <div ref={panelRef} data-portal-panel="" style={{
                    position: 'fixed', zIndex: 9999,
                    // Nejméně tak široký jako selektor, jinak podle nejdelší položky
                    // (ať je vidět celý text); maxWidth vázán na kotvu, ať nepřeteče okraj.
                    ...(coords.left != null
                        ? { left: coords.left, maxWidth: `calc(100vw - ${coords.left}px - 8px)` }
                        : { right: coords.right, maxWidth: `calc(100vw - ${coords.right}px - 8px)` }),
                    ...(coords.top != null ? { top: coords.top } : { bottom: coords.bottom }),
                    minWidth: coords.width, width: 'max-content',
                    background: panelBg,
                    backdropFilter: 'blur(24px) saturate(1.3)', WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
                    border: `1px solid ${border}`, borderRadius: 7,
                    boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
                    maxHeight: 280, overflowY: 'auto', padding: 4,
                }}>
                    {hledatelne && (
                        <div style={{ position: 'sticky', top: -4, background: panelBg, padding: '2px 2px 6px', margin: '-4px -4px 2px', borderBottom: `1px solid ${border}` }}>
                            <input
                                ref={searchRef}
                                value={q}
                                onChange={e => setQ(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && filtrovane.length) { onChange(filtrovane[0].value); setOpen(false); }
                                }}
                                placeholder="Hledat…"
                                className="w-full"
                                style={{ height: 30, padding: '0 8px', borderRadius: 5, fontSize: 13, color: 'var(--text-primary)', background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)', border: `1px solid ${border}`, outline: 'none' }}
                            />
                        </div>
                    )}
                    {filtrovane.map(o => {
                        if (o.hlavicka) {
                            return (
                                <div key={`h-${o.value ?? o.label}`} className="text-[10px] font-bold uppercase tracking-wide truncate"
                                    style={{ padding: '6px 10px 3px', color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)' }}>
                                    {o.label}
                                </div>
                            );
                        }
                        const on = String(o.value) === String(value ?? '');
                        const uroven = o.uroven || 0;
                        return (
                            <button key={String(o.value)} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
                                className="w-full flex items-center gap-2 text-left"
                                style={{ padding: '7px 10px', paddingLeft: 10 + uroven * 14, borderRadius: 5, background: on ? (ACCENT + (isDark ? '26' : '20')) : 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: on ? ACCENT : 'var(--text-primary)', fontWeight: on ? 600 : 400, opacity: o.tlumene ? 0.55 : 1 }}
                                onMouseEnter={e => { if (!on) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
                                onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
                                {/* Vodicí linka — bez ní odsazení vypadá jako náhodná mezera */}
                                {uroven > 0 && (
                                    <span aria-hidden="true" style={{ width: 8, height: 1, background: border, flexShrink: 0, marginLeft: -6 }} />
                                )}
                                {o.ikona && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{o.ikona}</span>}
                                <span className="flex-1 min-w-0">
                                    <span className="block truncate">{o.label}</span>
                                    {o.popis && (
                                        <span className="block truncate text-[10px]" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)', fontWeight: 400 }}>
                                            {o.popis}
                                        </span>
                                    )}
                                </span>
                                {o.odznaky}
                                {on && <Check size={14} style={{ color: ACCENT, flexShrink: 0 }} />}
                            </button>
                        );
                    })}
                    {filtrovane.length === 0 && <p className="text-xs text-center py-3" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)' }}>{options.length === 0 ? 'Žádné položky' : 'Nic nenalezeno'}</p>}
                </div>,
                document.body
            )}
        </div>
    );
}
